/**
 * Structural validation beyond the Zod schema: graph reachability, dangling references,
 * dead ends, role/dimension/variable references, duplicate ids.
 */
import { screenFitWarnings } from "./fit";
import type { Scenario, ScenarioNode } from "./schema";

export interface ValidationIssue {
  level: "error" | "warning";
  path: string;
  message: string;
}

export function nodeSuccessors(node: ScenarioNode): string[] {
  switch (node.type) {
    case "narrative":
    case "call":
    case "reveal":
    case "poll":
      return node.next ? [node.next] : [];
    case "decision":
      return node.options.map((o) => o.next);
    case "branch":
      return [...node.conditions.map((c) => c.next), node.else];
    case "debrief":
      return [];
  }
}

export function validateScenario(s: Scenario): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const err = (path: string, message: string) => issues.push({ level: "error", path, message });
  const warn = (path: string, message: string) => issues.push({ level: "warning", path, message });

  const nodeIds = new Set(Object.keys(s.nodes));
  const roleIds = new Set(s.roles.map((r) => r.id));
  const dimIds = new Set(s.dimensions.map((d) => d.id));
  const varIds = new Set(Object.keys(s.variables));

  if (roleIds.size !== s.roles.length) err("roles", "duplicate role ids");
  if (dimIds.size !== s.dimensions.length) err("dimensions", "duplicate dimension ids");
  if (!nodeIds.has(s.start)) err("start", `start node "${s.start}" does not exist`);

  // Reference checks.
  for (const [id, node] of Object.entries(s.nodes)) {
    for (const next of nodeSuccessors(node)) {
      if (!nodeIds.has(next)) err(`nodes.${id}`, `references missing node "${next}"`);
    }
    for (const roleId of Object.keys(node.roleNotes)) {
      if (!roleIds.has(roleId)) err(`nodes.${id}.roleNotes`, `unknown role "${roleId}"`);
    }
    if (node.type === "call" && node.callee.roleId && !roleIds.has(node.callee.roleId)) {
      err(`nodes.${id}.callee`, `unknown role "${node.callee.roleId}"`);
    }
    if (node.type === "decision") {
      const optIds = new Set(node.options.map((o) => o.id));
      if (optIds.size !== node.options.length) err(`nodes.${id}.options`, "duplicate option ids");
      if (!node.options.some((o) => o.quality === "best")) {
        warn(`nodes.${id}.options`, "no option is marked as best");
      }
      if (node.voters !== "all") {
        for (const r of node.voters) if (!roleIds.has(r)) err(`nodes.${id}.voters`, `unknown role "${r}"`);
      }
      for (const o of node.options) {
        for (const d of Object.keys(o.effects.score)) {
          if (!dimIds.has(d)) err(`nodes.${id}.options.${o.id}.effects.score`, `unknown dimension "${d}"`);
        }
        for (const v of Object.keys(o.effects.vars)) {
          if (!varIds.has(v)) err(`nodes.${id}.options.${o.id}.effects.vars`, `unknown variable "${v}"`);
        }
        for (const r of o.roleBonus) {
          if (!roleIds.has(r)) err(`nodes.${id}.options.${o.id}.roleBonus`, `unknown role "${r}"`);
        }
      }
    }
    if (node.type === "branch") {
      for (const c of node.conditions) {
        if (!varIds.has(c.var)) err(`nodes.${id}.conditions`, `unknown variable "${c.var}"`);
      }
    }
  }

  for (const inj of s.injects) {
    if (!nodeIds.has(inj.node)) err(`injects.${inj.id}`, `references missing node "${inj.node}"`);
    for (const a of inj.availableAfter) {
      if (!nodeIds.has(a)) err(`injects.${inj.id}.availableAfter`, `references missing node "${a}"`);
    }
  }
  const injectIds = new Set(s.injects.map((i) => i.id));
  if (injectIds.size !== s.injects.length) err("injects", "duplicate inject ids");

  for (const b of s.badges) {
    const c = b.condition;
    if (c.kind === "teamVarBelow" && !varIds.has(c.var)) err(`badges.${b.id}`, `unknown variable "${c.var}"`);
    if (c.kind === "teamDimension" && !dimIds.has(c.dimension)) {
      err(`badges.${b.id}`, `unknown dimension "${c.dimension}"`);
    }
  }
  const badgeIds = new Set(s.badges.map((b) => b.id));
  if (badgeIds.size !== s.badges.length) err("badges", "duplicate badge ids");

  // Reachability from start (plus inject entry points) and termination.
  const reachable = new Set<string>();
  const stack = [s.start, ...s.injects.map((i) => i.node)].filter((id) => nodeIds.has(id));
  while (stack.length) {
    const id = stack.pop()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    const node = s.nodes[id];
    if (node) for (const n of nodeSuccessors(node)) if (nodeIds.has(n) && !reachable.has(n)) stack.push(n);
  }
  for (const id of nodeIds) {
    if (!reachable.has(id)) warn(`nodes.${id}`, "node is unreachable from start or any inject");
  }
  const debriefs = [...nodeIds].filter((id) => s.nodes[id]?.type === "debrief");
  if (debriefs.length === 0) err("nodes", "scenario has no debrief node");

  // Every reachable node must be able to reach a debrief (no dead ends).
  const reversed = new Map<string, string[]>();
  for (const [id, node] of Object.entries(s.nodes)) {
    for (const n of nodeSuccessors(node)) {
      const arr = reversed.get(n) ?? [];
      arr.push(id);
      reversed.set(n, arr);
    }
  }
  const canFinish = new Set<string>();
  const q = [...debriefs];
  while (q.length) {
    const id = q.pop()!;
    if (canFinish.has(id)) continue;
    canFinish.add(id);
    for (const p of reversed.get(id) ?? []) if (!canFinish.has(p)) q.push(p);
  }
  for (const id of reachable) {
    const node = s.nodes[id];
    if (!node) continue;
    if (node.type !== "debrief" && nodeSuccessors(node).length === 0) {
      err(`nodes.${id}`, "dead end: node has no successor and is not a debrief");
    } else if (!canFinish.has(id)) {
      err(`nodes.${id}`, "node cannot reach a debrief");
    }
  }

  // Warn (never error) about nodes too long to fit the projector without scrolling.
  issues.push(...screenFitWarnings(s));

  return issues;
}

export function assertValidScenario(s: Scenario): void {
  const errors = validateScenario(s).filter((i) => i.level === "error");
  if (errors.length) {
    throw new Error(
      `Scenario "${s.id}" is invalid:\n${errors.map((e) => `  - ${e.path}: ${e.message}`).join("\n")}`,
    );
  }
}
